
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  watch,
  nextTick,
} from "vue";
import { Popover, message } from "ant-design-vue";
import { apiGetGoodsPaymentWay, apiOrderGoods } from "@api/gamelibs";
import { useloginStore } from "@/store/login";
import { nested } from "@pb/index.json";
import type { yunpb } from "@/types/protobuf";
import { localCache } from "@/js/localCache";

import Airwallex from "airwallex-payment-elements";
import { useGameListStore } from "@/store/gameList";
import { useRouter } from "vue-router";
const BuyType_StoreGame = 7;
const BuyType_Recharge = 1;

const GoodsPaymentWayKind = nested.yunpb.nested.GoodsPaymentWayKind.values;
const BuyTypeMap = {
  // 1 是充值 2是商城购买 参考 store.ext.proto 的 GoodsPaymentWayKind
  [GoodsPaymentWayKind.GPWK_Recharge]: BuyType_Recharge,
  [GoodsPaymentWayKind.GPWK_OrderGoods]: BuyType_StoreGame,
};

const PAY_CHANNEL_PC_WEB = 6;

type ICountryPayment = {
  paymentCountryName: string;
  paymentWayList: yunpb.IThirdPaymentWay[];
};

type ISelectPaymentType = "gemPay" | "googlePay" | "thirdPay";

type IReactive = {
  paymentData: yunpb.IGoodsPaymentWays;
  CountryPayment: ICountryPayment;
  // 选择的支付方式
  selectPaymentType: ISelectPaymentType;
  // 选择的支付方式id
  selectPaymentTypeId: number;
  // 选择的第三方支付的项
  selectThirdPaymentItem: yunpb.IThirdPaymentWay;
  // 第三方支付父盒子dom
  selectCountryThirdPaymentDom: any;
};
export default defineComponent({
  components: {
    Popover,
  },
  props: {
    kind: {
      type: Number,
      default: -1,
    },
    goodsId: {
      type: Number,
      default: -1,
    },
    gameId: {
      type: Number,
      default: 0,
    },
  },
  name: "PaymentPanel",
  emits: ["gotoRecharge"],
  setup(props) {
    const { loadAirwallex, redirectToCheckout } = Airwallex;
    const loginStore = useloginStore();
    const gameListStore = useGameListStore();
    const router = useRouter();
    const data = reactive<IReactive>({
      paymentData: {},
      CountryPayment: {
        paymentCountryName: "",
        paymentWayList: [],
      } as ICountryPayment,
      selectPaymentType: "gemPay",
      selectPaymentTypeId: -1, // selectPaymentType 与 selectPaymentTypeId 用来协同判断支付方式，因为宝石支付的id可能和第三方支付id出现一致的，所以多加一个selectselectPaymentType区分
      selectThirdPaymentItem: {},
      selectCountryThirdPaymentDom: null,
    });
    console.log("PaymentPanel", props, data);
    // 获取选中的国家代码
    const getSelectPaymentCountryName = computed<string>(
      () =>
        data.CountryPayment.paymentCountryName ||
        (data.paymentData.thirdPaymentPlatformList![0].countryWayList![0]
          .paymentCountryName as string)
    );

    // 获取选中的国家内第三方支付列表数据
    const getSelectThirdPayment = computed<yunpb.IThirdPaymentWay[]>(() => {
      return data.CountryPayment.paymentWayList.length
        ? data.CountryPayment.paymentWayList
        : (data.paymentData.thirdPaymentPlatformList![0].countryWayList![0]
            .paymentWayList! as yunpb.IThirdPaymentWay[]);
    });

    // 是否选中了宝石支付
    const isSelectGemPay = computed(() => {
      if (!data.paymentData?.gemPay) return false;
      return (
        data.selectPaymentType === "gemPay" &&
        data.selectPaymentTypeId === data.paymentData!.gemPay!.thirdPaymentWayId
      );
    });
    const isSelectGooglePay = computed(() => {
      return (
        data.selectPaymentType === "googlePay" &&
        data.selectPaymentTypeId ===
          data.paymentData.googlePayWay?.thirdPaymentWayId
      );
    });

    // 当想选中宝石支付时，检查余额是否足够
    const isEnoughWhenSelectGemPay = computed(() => {
      if (!data.paymentData?.gemPay) return false;
      if (loginStore.gems < +data.paymentData.gemPay!.amount!) return false;
      return true;
    });

    // 获取该商品id下的可用支付方式
    const getPaymentWay = async () => {
      console.log("执行次数");
      if (!props.goodsId || !props.kind) return;
      try {
        const res = await apiGetGoodsPaymentWay({
          goodsId: props.goodsId,
          kind: props.kind,
        });
        console.log("getPaymentWay", res.data);
        // 每次都要取消之前的选择
        data.selectPaymentTypeId = -1;
        data.paymentData = res.data!;

        // 尝试选择宝石支付方式
        // selectPayment("gemPay", data.paymentData?.gemPay?.thirdPaymentWayId);
      } catch (err) {
        console.error(err);
      }
    };

    // 切换第三方国家支付数据
    const changeCountryPayment = (item: yunpb.IThirdPaymentCountryWay) => {
      data.CountryPayment.paymentCountryName = item.paymentCountryName!;
      data.CountryPayment.paymentWayList = item.paymentWayList!;
    };
    // 选择第三方支付
    const selectPayment = (
      type: ISelectPaymentType,
      item: yunpb.IThirdPaymentWay
    ) => {
      console.log("selectPayment", type, item);

      data.selectPaymentType = type;
      if (type === "gemPay") {
        // 宝石余额不足
        if (!isEnoughWhenSelectGemPay.value) {
          data.selectPaymentTypeId = -1;
          // message.info("Insufficient balance, please recharge");
          return false;
        }
        data.selectThirdPaymentItem = {};
        data.selectPaymentTypeId = item as number;
      } else if (type === "googlePay") {
        data.selectPaymentTypeId = item.thirdPaymentWayId;
        data.selectThirdPaymentItem = {
          ...item,
          paymentPlatform: 4,
          paymentType: "googlePay",
        };
      } else {
        data.selectThirdPaymentItem = item;
        data.selectPaymentTypeId = item.thirdPaymentWayId;
      }
    };

    // 下单前的检查
    const orderGoods = () => {
      // -1 说明没有选择任何支付方式
      if (data.selectPaymentTypeId === -1)
        return message.info("Please select a payment method");
      // 选中宝石支付时，检查余额
      if (isSelectGemPay.value && !isEnoughWhenSelectGemPay.value) {
        return message.info("Insufficient balance");
      }
      handleOrderGoods();
    };

    // 下单请求
    const handleOrderGoods = async () => {
      try {
        const paymentWay = isSelectGemPay.value
          ? { ...data.paymentData.gemPay, paymentPlatform: 3 }
          : data.selectThirdPaymentItem;
        const res = await apiOrderGoods({
          goodsId: props.goodsId,
          buyNum: 1,
          goodsBuyType: BuyTypeMap[props.kind],
          payChannel: PAY_CHANNEL_PC_WEB,
          goodsSource: 5,
          paymentWay: {
            paymentPlatform: paymentWay!.paymentPlatform,
            countryCode: paymentWay!.paymentCountryCode,
            paymentType: paymentWay!.paymentType,
            currency: paymentWay!.currency,
            paymentWayId: paymentWay!.thirdPaymentWayId,
            kind: props.kind,
            frontCallbackUrl: location.href,
          },
        });
        if (isSelectGooglePay.value) {
          if (res.paymentInfo) {
            // gameListStore.updateGoogleAirWallexPayData({
            //   clientSecret: res.paymentInfo.clientSecret as string,
            //   tradeNo: res.paymentInfo.tradeNo as string,
            //   currency: paymentWay.currency as string,
            // });
            // toAirWallexPay();
            toAirWallexPay(
              res.paymentInfo.clientSecret as string,
              res.paymentInfo.tradeNo as string,
              paymentWay.currency as string
            );
          }
        } else if (!isSelectGemPay.value) {
          window.open(res.paymentInfo!.RedirectUrl!, "_blank");
        }
        console.log(
          "gameId:",
          props.gameId,
          "goodsId:",
          props.goodsId,
          "orderId:",
          res.orderInfo?.orderId,
          "setLocalStoreage buyGoodsInfo"
        );

        if (props.goodsId && res.orderInfo?.orderId) {
          localCache.setItem(
            "buyGoodsInfo",
            {
              gameId: props.gameId || 0,
              goodsId: props.goodsId,
              orderId: res.orderInfo.orderId,
            },
            1
          );
        }
        console.log("orderGoods", res);
      } catch (err: any) {
        message.error(err?.rpcOut?.desc);
        console.error(err);
      }
    };
    const toAirWallexPay = (
      clientSecret: string,
      tradeNo: string,
      currency: string
    ) => {
      const target = router.resolve({
        path: "/AirWallexPay",
        query: {
          clientSecret,
          tradeNo,
          currency,
        },
      });
      window.open(target.href, "_blank");
      // router.push({ name: "AirWallexPay" });
      // const env = location.href.includes("/alpha");
      // const successUrl = env
      //   ? `http://www.chikiigame.com/m/alpha/chikiiwebpre/index.html#/GameLibrary`
      //   : `https://www.chikiigame.com/m/chikiiwebprod/index.html#/GameLibrary`;
      // loadAirwallex({
      //   env: env ? "demo" : "prod",
      //   origin: window.location.origin,
      // }).then(() => {
      //   redirectToCheckout({
      //     client_secret: client_secret,
      //     intent_id: intent_id,
      //     env: env,
      //     mode: "payment",
      //     currency,
      //     methods: ["googlepay"],
      //     successUrl,
      //     logoUrl:
      //       "https://www.chikiigame.com/m/alpha/worldCup/img/logo-default.648d1475.png",
      //     googlePayRequestOptions: {
      //       countryCode: "SG",
      //     },
      //   });
      // });
    };

    // 变化后需要重新拉取支付数据
    watch(
      () => [props.goodsId, props.kind],
      (newV, oldV: any) => {
        const [newVGoodsId, newVKind] = newV;
        const [oldVGoodsId, oldVKind] = oldV || [];
        console.log(
          "watch goodsid kind",
          newVGoodsId,
          oldVGoodsId,
          newVKind,
          oldVKind
        );
        getPaymentWay();
      },
      { immediate: true }
    );
    onMounted(() => {
      console.log("执行次数2");
      nextTick(() => {
        console.log(
          "data.selectCountryThirdPaymentDom",
          data.selectCountryThirdPaymentDom
        );
      });
    });
    return {
      loginStore,
      changeCountryPayment,
      getSelectPaymentCountryName,
      getSelectThirdPayment,
      selectPayment,
      orderGoods,
      isSelectGemPay,
      isSelectGooglePay,
      isEnoughWhenSelectGemPay,
      ...toRefs(data),
      GoodsPaymentWayKind,
    };
  },
});
